import { Fragment } from 'react'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { Box, Paper } from '@mui/material'
import { Attribute, AttributeLabel, AttributeValue } from 'packages/eid-ui'
import { useAccessRequestPolicy } from 'hooks'
import { useTranslation } from 'react-i18next'
import { ResponsibleParty } from 'components'
import useSubcomponents from 'useSubcomponents'
import { ResourceOwnersList } from 'components/Owners/ResourceOwnersList'
import { ResourceApproversList } from 'components/Owners/ResourceApproversList'

const smallScreenWidth = 960
const canSeeOwnersControlName =
    'ResourceAdmin-ApplicationDetails-AppManagementRole-Overview-Owners-Control'
const canSeeApproversControlName =
    'ResourceAdmin-ApplicationDetails-AppManagementRole-Overview-Approvers-Control'

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            position: 'relative',
            padding: theme.spacing(2),
            display: 'flex',
            miHeight: '156px',
            justifyContent: 'space-between',

            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                flexDirection: 'column',
            },
        },
        basicInfo: {
            display: 'flex',
            flexDirection: 'column',
        },
        azureApplicationDetailsLabel: {
            color: '#1b1f1f',
            textDecoration: 'underline',
        },

        azureApplicationDetailsIdValues: {
            color: theme?.palette?.primary?.main,
        },

        content: {
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            [`@media (max-width:345px)`]: {
                flexDirection: 'column-reverse',
            },
            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                display: 'block',
            },
        },
        attribute: {
            marginTop: '-8px',
        },
        attributeLabel: {
            minWidth: '265px',
        },
        attributeValue: {
            wordBreak: 'unset',
        },
    }),
)

export const Overview = (props) => {
    const { role } = props
    const classes = useStyles()
    const { t } = useTranslation()

    const { getAccessBasedAttributes, hasAccessToControl } = useSubcomponents()
    const attributes = [
        {
            label: 'TechnicalName',
            value: 'name',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppManagementRole-TechnicalName',
            },
        },
        {
            label: 'Type',
            value: 'managementRoleTypeFriendlyName',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppManagementRole-Type',
            },
        },
        {
            label: 'SystemType',
            value: 'resourceTypeFriendlyName',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppManagementRole-SystemType',
            },
        },
        {
            label: 'Description',
            value: 'description',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppManagementRole-Description',
            },
        },
        {
            label: 'IsHighSecurity',
            render: (data) => {
                return data && data.isHighSecurity ? t('Yes') : t('No')
            },
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppManagementRole-IsHighSecurity',
            },
        },
        {
            label: 'ManagementRoleDefinition',
            value: 'managementRoleDefinitionFriendlyName',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppManagementRole-ManagementRoleDefinition',
            },
        },
        {
            label: 'PublishInIAMShop',
            render: (data) => {
                return data && data.requestable ? t('Yes') : t('No')
            },
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppManagementRole-PublishedInIAmShop',
            },
        },
    ]
    const policyAttributes = [
        {
            label: 'AccessRequestPolicy',
            value: 'name',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppManagementRole-AccessRequestPolicy',
            },
        },
        {
            label: 'AllowsTimeConstrainedAccess',
            render: (policy) =>
                policy && policy.isTimeConstrained ? t('Yes') : t('No'),
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppManagementRole-AllowsTimeConstrainedAccess',
            },
        },
        {
            label: 'DefaultAccessDuration',
            value: 'defaultValueInMinutes',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppManagementRole-DefaultAccessDuration',
            },
        },
        {
            label: 'MaxAccessDuration',
            value: 'maximumValueInMinutes',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationDetails-AppManagementRole-MaxAccessDuration',
            },
        },
    ]
    const policy = useAccessRequestPolicy(role && role.requestPolicyId)

    const roleDetail = (
        <Box>
            {attributes &&
                getAccessBasedAttributes(attributes).map((att, i) => {
                    return (
                        <Attribute
                            className={classes.attribute}
                            orientation="horizontal"
                            key={att.value + i}
                        >
                            <AttributeLabel className={classes.attributeLabel}>
                                {t(att.label)}
                            </AttributeLabel>
                            <AttributeValue className={classes.attributeValue}>
                                {att.render
                                    ? att.render(role)
                                    : role && role[att.value]}
                            </AttributeValue>
                        </Attribute>
                    )
                })}
        </Box>
    )
    const policyDetails = (
        <Box>
            {policyAttributes &&
                getAccessBasedAttributes(policyAttributes).map((att, i) => {
                    return (
                        <Attribute
                            className={classes.attribute}
                            orientation="horizontal"
                            key={att.value + i}
                        >
                            <AttributeLabel className={classes.attributeLabel}>
                                {t(att.label)}
                            </AttributeLabel>
                            <AttributeValue className={classes.attributeValue}>
                                {att.render
                                    ? att.render(policy)
                                    : policy && policy[att.value]}
                            </AttributeValue>
                        </Attribute>
                    )
                })}
        </Box>
    )

    const renderContent = () => (
        <Box className={classes.content}>
            <Box>
                {roleDetail}
                {policyDetails}
            </Box>

            <Box>
                {hasAccessToControl(canSeeOwnersControlName) && (
                    <Box data-protectedsubcomponent={canSeeOwnersControlName}>
                        <ResourceOwnersList
                            resourceId={role?.id}
                            label="ManagementRoles_Owners"
                            contextKey="RequestViewApplicationManagementRolesOwners"
                        />
                    </Box>
                )}
                {hasAccessToControl(canSeeApproversControlName) && (
                    <Box
                        data-protectedsubcomponent={canSeeApproversControlName}
                    >
                        <ResourceApproversList
                            resourceId={role?.id}
                            label="Approvers"
                            contextKey="RequestViewApplicationManagementRolesApprovers"
                        />
                    </Box>
                )}
                {role?.ownerId && role?.ownerIsPerson ? (
                    <ResponsibleParty responsiblePersonId={role?.ownerId} />
                ) : (
                    <Attribute>
                        <AttributeLabel>{t('ResponsibleParty')}</AttributeLabel>
                        <AttributeValue>
                            {role?.ownerFriendlyName || '-'}
                        </AttributeValue>
                    </Attribute>
                )}
            </Box>
        </Box>
    )
    return (
        <>
            <Paper
                style={{
                    padding: '32px',
                    width: 'fit-content',
                    minWidth: '100%',
                }}
            >
                {renderContent()}
            </Paper>
        </>
    )
}
